/**
 * Shared utilities for Directive
 */

// ============================================================================
// Deep freeze
// ============================================================================

/**
 * Recursively `Object.freeze` an object including nested objects, arrays, and
 * array elements. Uses a `WeakSet` to handle cycles. Skips primitives and
 * already-frozen values to avoid wasted work.
 *
 * Used at definition-registration sites (constraints, derivations, effects,
 * events, prefixed specs) so post-registration mutation of a nested operand
 * cannot silently change the compiled closure's behavior. Prefer
 * {@link freezeSpec} at registration sites — it documents the convention.
 */
export function deepFreeze<T>(
  value: T,
  seen: WeakSet<object> = new WeakSet(),
): T {
  if (value === null || typeof value !== "object") {
    return value;
  }
  const obj = value as unknown as object;
  if (seen.has(obj) || Object.isFrozen(obj)) {
    return value;
  }
  seen.add(obj);

  if (Array.isArray(obj)) {
    for (const item of obj) {
      deepFreeze(item, seen);
    }
  } else {
    for (const key of Object.keys(obj)) {
      deepFreeze((obj as Record<string, unknown>)[key], seen);
    }
  }

  Object.freeze(obj);
  return value;
}

/**
 * Freeze a definition spec at registration. Centralizes the deepFreeze
 * convention so a new definition arm cannot forget it — every constraint,
 * derivation, effect, event, and prefixed-spec registration funnels through
 * this single helper.
 *
 * @param spec - The definition spec to freeze (mutated in place, returned)
 * @returns The same `spec` value, now deeply frozen
 *
 * @example
 * ```ts
 * // At registration time, freeze user-supplied specs so post-registration
 * // mutation cannot silently change the compiled closure's behavior.
 * const spec = freezeSpec(userPredicate);
 * memoizePredicate(spec);
 * ```
 */
export function freezeSpec<T>(spec: T): T {
  return deepFreeze(spec);
}

/**
 * Wrap a synthesized definition function so a `[Directive]`-prefixed error
 * thrown from inside it is re-thrown with the owning definition's category +
 * id injected, and the original error preserved as `cause`. Non-`[Directive]`
 * errors (user code, native) pass through untouched.
 *
 * Centralizes the owner-attribution wrap shared by the data-form `when`
 * (constraints), `on` (effects), and `compute` (derivations) closures — a
 * `[Directive]`-prefixed throw (e.g. `$matches: string`) is re-pointed at the
 * owning definition so the stack trace blames user config, not the runtime.
 *
 * @param category - The owning definition kind, used in the re-thrown message.
 * @param id - The owning definition's id.
 * @param fn - The synthesized function to wrap.
 * @returns A function with identical signature that re-attributes Directive errors.
 */
export function attributeError<A extends unknown[], R>(
  category: "constraint" | "effect" | "derivation",
  id: string,
  fn: (...args: A) => R,
): (...args: A) => R {
  // Synchronous by design: the wrapped definition functions (memoized
  // predicate `when`/`on` closures, template closures) always return
  // synchronously. A `try/catch` only re-attributes a synchronous throw — an
  // async-returning fn's rejected promise would escape un-re-attributed.
  return (...args: A): R => {
    try {
      return fn(...args);
    } catch (e) {
      if (e instanceof Error && e.message.startsWith("[Directive] ")) {
        throw new Error(
          `[Directive] ${category} '${id}': ${e.message.slice("[Directive] ".length)}`,
          { cause: e },
        );
      }
      throw e;
    }
  };
}

/**
 * Execute a promise with a timeout, properly cleaning up the timer.
 * Used by both constraints and resolvers for timeout handling.
 *
 * @param promise - The promise to wrap with a timeout
 * @param ms - Timeout duration in milliseconds
 * @param errorMessage - Error message if timeout occurs
 * @returns The promise result
 * @throws Error if timeout is exceeded
 */
/**
 * Symbol marker attached to the Error thrown when {@link withTimeout}
 * fires. The retry path in `resolvers.ts` reads this to construct
 * `ShouldRetryContext.reason = "timeout"` so user-supplied
 * `shouldRetry(err, attempt, ctx)` can branch on `ctx.reason === "timeout"`.
 *
 * Non-enumerable so it doesn't leak into JSON serialization of the
 * thrown error.
 *
 * @internal
 */
export const TIMEOUT_MARKER: unique symbol = Symbol.for(
  "@directive-run/core.timeout-marker",
);

export async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  errorMessage: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      const err = new Error(errorMessage);
      Object.defineProperty(err, TIMEOUT_MARKER, {
        value: true,
        enumerable: false,
        configurable: false,
        writable: false,
      });
      reject(err);
    }, ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId!);
  }
}

/**
 * Normalize an error to an Error instance.
 * Ensures consistent error handling throughout the library.
 *
 * @param error - The error to normalize (can be anything)
 * @returns An Error instance
 */
export function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
}

/**
 * Create a stable JSON string with sorted keys.
 * Handles circular references and deeply nested objects safely.
 *
 * @param value - The value to stringify
 * @param maxDepth - Maximum nesting depth (default: 50)
 * @returns A stable JSON string
 */
export function stableStringify(value: unknown, maxDepth = 50): string {
  const seen = new WeakSet();

  /** Stringify a primitive value (null, undefined, string, number, boolean, function, symbol). */
  function stringifyPrimitive(val: unknown): string | undefined {
    if (val === null) return "null";
    if (val === undefined) return "undefined";

    const type = typeof val;
    if (type === "string") return JSON.stringify(val);
    if (type === "number" || type === "boolean") return String(val);
    if (type === "function") return '"[function]"';
    if (type === "symbol") return '"[symbol]"';

    return undefined;
  }

  /** Guard against circular references using a seen-set, then delegate to fn. */
  function withCircularGuard(obj: object, fn: () => string): string {
    if (seen.has(obj)) {
      return '"[circular]"';
    }
    seen.add(obj);
    const result = fn();
    seen.delete(obj);

    return result;
  }

  /** Stringify an array with circular reference protection. */
  function stringifyArray(val: unknown[], depth: number): string {
    return withCircularGuard(
      val,
      () => `[${val.map((v) => stringify(v, depth + 1)).join(",")}]`,
    );
  }

  /** Stringify an object with sorted keys and circular reference protection. */
  function stringifyObject(
    obj: Record<string, unknown>,
    depth: number,
  ): string {
    return withCircularGuard(obj, () => {
      const keys = Object.keys(obj).sort();
      const pairs = keys.map(
        (k) => `${JSON.stringify(k)}:${stringify(obj[k], depth + 1)}`,
      );

      return `{${pairs.join(",")}}`;
    });
  }

  /** Recursively stringify a value with depth limit and circular detection. */
  function stringify(val: unknown, depth: number): string {
    if (depth > maxDepth) {
      return '"[max depth exceeded]"';
    }

    // BigInt is not JSON-serializable; encode with a trailing 'n' to remain
    // distinct from numeric strings.
    if (typeof val === "bigint") {
      return `${val.toString()}n`;
    }

    const primitive = stringifyPrimitive(val);
    if (primitive !== undefined) {
      return primitive;
    }

    // Typed-value branches: each prefix is bare (no outer quotes) so it can
    // never collide with a JSON-encoded string. `JSON.stringify("D:...")`
    // emits `"D:..."` (the wrapping quotes ARE part of the output string),
    // whereas the Date branch emits `D:...` (no quotes). Distinct outputs.
    if (val instanceof Date) {
      return `D:${val.toISOString()}`;
    }
    if (val instanceof RegExp) {
      return `R:${val.source}:${val.flags}`;
    }
    if (val instanceof Map) {
      const entries = [...val.entries()].sort();
      return `M:${stringify(entries, depth + 1)}`;
    }
    if (val instanceof Set) {
      const items = [...val].sort();
      return `S:${stringify(items, depth + 1)}`;
    }

    if (Array.isArray(val)) {
      return stringifyArray(val, depth);
    }

    if (typeof val === "object") {
      return stringifyObject(val as Record<string, unknown>, depth);
    }

    return '"[unknown]"';
  }

  return stringify(value, 0);
}

/**
 * Check for prototype pollution in an object, including nested objects.
 * Returns true if the object is safe, false if dangerous keys are found.
 *
 * @param obj - The object to check
 * @param maxDepth - Maximum nesting depth to check (default: 50)
 * @returns True if safe, false if dangerous keys found
 */
export function isPrototypeSafe(obj: unknown, maxDepth = 50): boolean {
  const dangerousKeys = new Set(["__proto__", "constructor", "prototype"]);
  const seen = new WeakSet();

  /** Guard against circular references using a seen-set, then delegate to fn. */
  function withCircularGuard(objVal: object, fn: () => boolean): boolean {
    if (seen.has(objVal)) return true;
    seen.add(objVal);
    const result = fn();
    seen.delete(objVal);

    return result;
  }

  /** Check array elements for prototype pollution keys. */
  function checkArray(arr: unknown[], depth: number): boolean {
    for (const item of arr) {
      if (!check(item, depth + 1)) {
        return false;
      }
    }

    return true;
  }

  /** Check object keys and values for prototype pollution. */
  function checkObject(
    objVal: Record<string, unknown>,
    depth: number,
  ): boolean {
    for (const key of Object.keys(objVal)) {
      if (dangerousKeys.has(key)) {
        return false;
      }
      if (!check(objVal[key], depth + 1)) {
        return false;
      }
    }

    return true;
  }

  /** Recursively check a value tree for dangerous prototype keys. */
  function check(val: unknown, depth: number): boolean {
    if (depth > maxDepth) return false; // Fail safe at max depth - don't assume safety
    if (val === null || val === undefined) return true;
    if (typeof val !== "object") return true;

    const objVal = val as Record<string, unknown>;

    if (Array.isArray(objVal)) {
      return withCircularGuard(objVal, () => checkArray(objVal, depth));
    }

    return withCircularGuard(objVal, () => checkObject(objVal, depth));
  }

  return check(obj, 0);
}

/**
 * Shallow equality comparison for objects.
 * Used by React hooks to avoid unnecessary re-renders.
 *
 * @param a - First object
 * @param b - Second object
 * @returns True if objects are shallowly equal
 */
export function shallowEqual<T extends Record<string, unknown>>(
  a: T,
  b: T,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }

  return true;
}

/**
 * Generate a simple hash string from an object.
 * Uses djb2 algorithm on the stable stringified value.
 *
 * **Limitations:**
 * - 32-bit hash output means collision probability increases with data set size
 *   (birthday paradox: ~50% collision chance at ~77,000 distinct values)
 * - Suitable for: cache invalidation, change detection, deduplication of small sets
 * - NOT suitable for: cryptographic use, security-sensitive operations, large-scale deduplication
 *
 * For security-sensitive use cases requiring stronger collision resistance,
 * consider using a cryptographic hash like SHA-256.
 *
 * @param value - The value to hash
 * @returns A hex hash string (8 characters, 32 bits)
 */
export function hashObject(value: unknown): string {
  const str = stableStringify(value);
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit and then to hex
  return (hash >>> 0).toString(16);
}

// ============================================================================
// Distributable Snapshot Utilities
// ============================================================================

/**
 * Distributable snapshot type for type-safe helper functions.
 */
export interface DistributableSnapshotLike<T = Record<string, unknown>> {
  data: T;
  createdAt: number;
  expiresAt?: number;
  version?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Check if a distributable snapshot has expired.
 * Returns false if the snapshot has no expiresAt field.
 *
 * @example
 * ```typescript
 * const snapshot = system.getDistributableSnapshot({ ttlSeconds: 3600 });
 * // ... later ...
 * if (isSnapshotExpired(snapshot)) {
 *   // Refresh the snapshot
 * }
 * ```
 *
 * @param snapshot - The snapshot to check
 * @param now - Optional current timestamp (defaults to Date.now())
 * @returns True if the snapshot has expired, false otherwise
 */
export function isSnapshotExpired<T>(
  snapshot: DistributableSnapshotLike<T>,
  now: number = Date.now(),
): boolean {
  return snapshot.expiresAt !== undefined && now > snapshot.expiresAt;
}

/**
 * Validate a distributable snapshot and return its data.
 * Throws if the snapshot is malformed or has expired.
 *
 * @example
 * ```typescript
 * const cached = JSON.parse(await redis.get(`entitlements:${userId}`));
 * try {
 *   const data = validateSnapshot(cached);
 *   // Use data.canUseFeature, etc.
 * } catch (e) {
 *   // Snapshot invalid or expired, refresh it
 * }
 * ```
 *
 * @example Using custom timestamp for testing
 * ```typescript
 * const snapshot = { data: { test: true }, createdAt: 1000, expiresAt: 2000 };
 * validateSnapshot(snapshot, 1500); // Returns { test: true }
 * validateSnapshot(snapshot, 2500); // Throws: Snapshot expired
 * ```
 *
 * @param snapshot - The snapshot to validate
 * @param now - Optional current timestamp (defaults to Date.now())
 * @returns The snapshot data if valid
 * @throws Error if the snapshot is malformed or has expired
 */
export function validateSnapshot<T>(
  snapshot: DistributableSnapshotLike<T>,
  now: number = Date.now(),
): T {
  // Structural validation
  if (!snapshot || typeof snapshot !== "object") {
    throw new Error(
      "[Directive] Invalid snapshot: expected an object with 'data' and 'createdAt' properties.",
    );
  }
  if (!("data" in snapshot)) {
    throw new Error(
      "[Directive] Invalid snapshot: missing required 'data' property.",
    );
  }
  if (!("createdAt" in snapshot) || typeof snapshot.createdAt !== "number") {
    throw new Error(
      "[Directive] Invalid snapshot: missing or invalid 'createdAt' property (expected number).",
    );
  }

  // Expiration validation
  if (isSnapshotExpired(snapshot, now)) {
    const expiredAt = new Date(snapshot.expiresAt!).toISOString();
    throw new Error(
      `[Directive] Snapshot expired at ${expiredAt}. Obtain a fresh snapshot from the source.`,
    );
  }
  return snapshot.data;
}

/**
 * Diff result for a single changed value.
 */
export interface SnapshotDiffEntry {
  /** The key path that changed (e.g., "canUseApi" or "limits.apiCalls") */
  path: string;
  /** The value in the old snapshot */
  oldValue: unknown;
  /** The value in the new snapshot */
  newValue: unknown;
  /** Type of change: "added", "removed", or "changed" */
  type: "added" | "removed" | "changed";
}

/**
 * Result of diffing two snapshots.
 */
export interface SnapshotDiff {
  /** Whether the snapshots are identical */
  identical: boolean;
  /** List of changes between snapshots */
  changes: SnapshotDiffEntry[];
  /** Whether the version changed (if both have versions) */
  versionChanged: boolean;
  /** Old version (if available) */
  oldVersion?: string;
  /** New version (if available) */
  newVersion?: string;
}

/**
 * Compare two distributable snapshots and return the differences.
 * Useful for debugging, audit logs, and webhook payloads.
 *
 * @example
 * ```typescript
 * const oldSnapshot = system.getDistributableSnapshot({ includeVersion: true });
 * system.dispatch({ type: "upgradePlan", plan: "pro" });
 * const newSnapshot = system.getDistributableSnapshot({ includeVersion: true });
 *
 * const diff = diffSnapshots(oldSnapshot, newSnapshot);
 * if (!diff.identical) {
 *   console.log("Changes:", diff.changes);
 *   // [{ path: "canUseApi", oldValue: false, newValue: true, type: "changed" }]
 * }
 * ```
 *
 * @param oldSnapshot - The previous snapshot
 * @param newSnapshot - The new snapshot
 * @returns A diff result with all changes
 */
export function diffSnapshots<T = Record<string, unknown>>(
  oldSnapshot: DistributableSnapshotLike<T>,
  newSnapshot: DistributableSnapshotLike<T>,
): SnapshotDiff {
  const changes: SnapshotDiffEntry[] = [];

  /** Push a change entry to the diff results. */
  function pushChange(
    path: string,
    oldValue: unknown,
    newValue: unknown,
    type: SnapshotDiffEntry["type"],
  ): void {
    changes.push({ path, oldValue, newValue, type });
  }

  /** Handle null/undefined comparison cases. Returns true if fully handled. */
  function compareNullish(
    oldObj: unknown,
    newObj: unknown,
    path: string,
  ): boolean {
    if (oldObj === null || oldObj === undefined) {
      if (newObj !== null && newObj !== undefined) {
        pushChange(path, oldObj, newObj, "added");
      }

      return true;
    }
    if (newObj === null || newObj === undefined) {
      pushChange(path, oldObj, newObj, "removed");

      return true;
    }

    return false;
  }

  /** Compare two arrays element by element, recursing into each. */
  function compareArrays(
    oldArr: unknown[],
    newArr: unknown[],
    path: string,
  ): void {
    if (oldArr.length !== newArr.length) {
      pushChange(path, oldArr, newArr, "changed");

      return;
    }
    for (let i = 0; i < oldArr.length; i++) {
      compare(oldArr[i], newArr[i], `${path}[${i}]`);
    }
  }

  /** Compare two objects by key union, detecting added/removed/changed. */
  function compareObjects(
    oldRecord: Record<string, unknown>,
    newRecord: Record<string, unknown>,
    path: string,
  ): void {
    const allKeys = new Set([
      ...Object.keys(oldRecord),
      ...Object.keys(newRecord),
    ]);

    for (const key of allKeys) {
      const childPath = path ? `${path}.${key}` : key;
      if (!(key in oldRecord)) {
        pushChange(childPath, undefined, newRecord[key], "added");
      } else if (!(key in newRecord)) {
        pushChange(childPath, oldRecord[key], undefined, "removed");
      } else {
        compare(oldRecord[key], newRecord[key], childPath);
      }
    }
  }

  /** Recursively compare two values and record differences. */
  function compare(oldObj: unknown, newObj: unknown, path: string): void {
    if (compareNullish(oldObj, newObj, path)) {
      return;
    }

    // Handle primitives
    if (typeof oldObj !== "object" || typeof newObj !== "object") {
      if (!Object.is(oldObj, newObj)) {
        pushChange(path, oldObj, newObj, "changed");
      }

      return;
    }

    // Handle arrays
    if (Array.isArray(oldObj) && Array.isArray(newObj)) {
      compareArrays(oldObj, newObj, path);

      return;
    }

    // Handle objects
    compareObjects(
      oldObj as Record<string, unknown>,
      newObj as Record<string, unknown>,
      path,
    );
  }

  // Compare data
  compare(oldSnapshot.data, newSnapshot.data, "");

  // Check version change
  const versionChanged =
    oldSnapshot.version !== newSnapshot.version &&
    (oldSnapshot.version !== undefined || newSnapshot.version !== undefined);

  return {
    identical: changes.length === 0,
    changes,
    versionChanged,
    oldVersion: oldSnapshot.version,
    newVersion: newSnapshot.version,
  };
}

// ============================================================================
// Snapshot Signing (HMAC)
// ============================================================================

/**
 * A signed distributable snapshot.
 * Contains the original snapshot plus a cryptographic signature.
 */
export interface SignedSnapshot<T = Record<string, unknown>>
  extends DistributableSnapshotLike<T> {
  /** HMAC-SHA256 signature in hex format */
  signature: string;
  /** Signing algorithm used */
  algorithm: "hmac-sha256";
}

/**
 * Check if a snapshot is signed.
 *
 * @param snapshot - The snapshot to check
 * @returns True if the snapshot has a signature
 */
export function isSignedSnapshot<T>(
  snapshot: DistributableSnapshotLike<T> | SignedSnapshot<T>,
): snapshot is SignedSnapshot<T> {
  return "signature" in snapshot && typeof snapshot.signature === "string";
}

/**
 * Sign a distributable snapshot using HMAC-SHA256.
 * Creates a tamper-proof signature that can be verified later.
 *
 * **Security Notes:**
 * - Use a cryptographically random secret of at least 32 bytes
 * - Store the secret securely (environment variable, secrets manager)
 * - Never expose the secret to clients
 * - The signature covers all snapshot fields for integrity
 *
 * @example
 * ```typescript
 * const snapshot = system.getDistributableSnapshot({
 *   includeDerivations: ['canUseFeature', 'limits'],
 *   ttlSeconds: 3600,
 * });
 *
 * // Sign the snapshot (server-side only)
 * const signed = await signSnapshot(snapshot, process.env.SNAPSHOT_SECRET);
 *
 * // Store in JWT, Redis, or send to client
 * const jwt = createJWT({ snapshot: signed });
 *
 * // Later, verify the signature
 * const isValid = await verifySnapshotSignature(signed, process.env.SNAPSHOT_SECRET);
 * if (!isValid) {
 *   throw new Error('Snapshot has been tampered with');
 * }
 * ```
 *
 * @param snapshot - The snapshot to sign
 * @param secret - The HMAC secret (string or Uint8Array)
 * @returns A signed snapshot with the signature attached
 */
export async function signSnapshot<T>(
  snapshot: DistributableSnapshotLike<T>,
  secret: string | Uint8Array,
): Promise<SignedSnapshot<T>> {
  // Create a canonical representation for signing
  const payload = stableStringify({
    data: snapshot.data,
    createdAt: snapshot.createdAt,
    expiresAt: snapshot.expiresAt,
    version: snapshot.version,
    metadata: snapshot.metadata,
  });

  const signature = await hmacSha256(payload, secret);

  return {
    ...snapshot,
    signature,
    algorithm: "hmac-sha256",
  };
}

/**
 * Verify the signature of a signed snapshot.
 * Returns true if the signature is valid, false otherwise.
 *
 * **Important:** Always verify signatures before trusting snapshot data,
 * especially if the snapshot was received from an untrusted source (client, cache).
 *
 * The default verification enforces `expiresAt` — a captured-and-replayed
 * snapshot stops verifying once its `expiresAt` is in the past. Callers
 * that legitimately need signature-only validation (testing infrastructure,
 * archival validation) can pass `{ ignoreExpiry: true }`.
 *
 * @example
 * ```typescript
 * // Receive signed snapshot from client or cache
 * const snapshot = JSON.parse(cachedData);
 *
 * // Verify before using. `verifySnapshotSignature` returns false for an
 * // expired snapshot even when the HMAC is valid; treat it as the
 * // single attestation gate.
 * const isValid = await verifySnapshotSignature(snapshot, process.env.SNAPSHOT_SECRET);
 * if (!isValid) {
 *   throw new Error('Invalid or expired snapshot');
 * }
 *
 * // Now safe to use snapshot.data
 * processSnapshotData(snapshot.data);
 * ```
 *
 * @param signedSnapshot - The signed snapshot to verify
 * @param secret - The HMAC secret (must match the signing secret)
 * @param options - Optional flags:
 *   - `ignoreExpiry`: skip the `expiresAt` check (signature-only mode)
 *   - `now`: override the current time used for expiry comparison
 *     (test helper)
 * @returns True if signature is valid AND (unless `ignoreExpiry`) not
 *   expired; false otherwise
 */
export async function verifySnapshotSignature<T>(
  signedSnapshot: SignedSnapshot<T>,
  secret: string | Uint8Array,
  options?: { ignoreExpiry?: boolean; now?: number },
): Promise<boolean> {
  if (!signedSnapshot.signature || signedSnapshot.algorithm !== "hmac-sha256") {
    return false;
  }

  // Recreate the canonical payload (same as signing)
  const payload = stableStringify({
    data: signedSnapshot.data,
    createdAt: signedSnapshot.createdAt,
    expiresAt: signedSnapshot.expiresAt,
    version: signedSnapshot.version,
    metadata: signedSnapshot.metadata,
  });

  const expectedSignature = await hmacSha256(payload, secret);

  // Use timing-safe comparison
  if (!timingSafeEqual(signedSnapshot.signature, expectedSignature)) {
    return false;
  }

  // Enforce expiresAt unless the caller explicitly opted out. Without
  // this check, a captured-and-replayed valid signature is accepted
  // forever — the canonical replay-attack chain. Treating the signature
  // as a full attestation gate requires checking the attestation window.
  if (
    !options?.ignoreExpiry &&
    isSnapshotExpired(signedSnapshot, options?.now)
  ) {
    return false;
  }

  return true;
}

/**
 * Create HMAC-SHA256 signature of a message.
 * Uses Web Crypto API for cross-platform support (Node.js, browsers, Deno, Bun).
 */
async function hmacSha256(
  message: string,
  secret: string | Uint8Array,
): Promise<string> {
  // Convert secret to Uint8Array if string
  const secretBytes: Uint8Array =
    typeof secret === "string" ? new TextEncoder().encode(secret) : secret;

  // Import key for HMAC
  const algorithm: HmacImportParams = {
    name: "HMAC",
    hash: { name: "SHA-256" },
  };
  const key = await crypto.subtle.importKey(
    "raw",
    secretBytes as unknown as ArrayBuffer,
    algorithm,
    false,
    ["sign"],
  );

  // Sign the message
  const messageBytes = new TextEncoder().encode(message);
  const signature = await crypto.subtle.sign("HMAC", key, messageBytes);

  // Convert to hex string
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Timing-safe string comparison to prevent timing attacks.
 * Both strings should be the same length (hex signatures from same algorithm).
 */
function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

/**
 * Safely stringify any value for display. Handles circular references,
 * BigInt, throwing toJSON, and optional truncation.
 *
 * This is the canonical implementation – all packages should import this
 * instead of maintaining local copies.
 */
export function safeStringify(data: unknown, maxLen = 500): string {
  try {
    const str = JSON.stringify(
      data,
      (_key, value) => {
        if (typeof value === "bigint") {
          return `${value}n`;
        }

        return value;
      },
      2,
    );
    if (!str) {
      return "[undefined]";
    }
    if (str.length <= maxLen) {
      return str;
    }

    return `${str.slice(0, maxLen)}\n... (truncated, ${str.length} chars total)`;
  } catch {
    return "[unserializable]";
  }
}
